import React from 'react';
import { Modal } from 'react-bootstrap';
import { FiX } from 'react-icons/fi';
import { useSelector } from 'react-redux';
import styled, { createGlobalStyle } from 'styled-components';
import { EXPORT_COLORS } from '../../constants';
import type { RootState } from '../../../../state/store';

interface BaseExportModalProps {
  show: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  showTwoColumns?: boolean;
  wide?: boolean;
}

export const BaseExportModal: React.FC<BaseExportModalProps> = ({
  show,
  onClose,
  title,
  children,
  footer,
  showTwoColumns,
  wide = false
}) => {
  const theme = useSelector((state: RootState) => state.theme.mode);
  const isDark = theme !== 'light';
  return (
    <>
      <ModalStyles showTwoColumns={showTwoColumns} wide={wide} />
      <Modal
        show={show}
        onHide={onClose}
        dialogClassName={`export-quota-modal ${showTwoColumns ? 'show-two-columns' : ''} ${wide ? 'export-quota-modal-wide' : ''}`}
        contentClassName="transparent-modal-content"
      >
        <StyledModalContent>
          <ContentCard $isDark={isDark}>
            <CustomCloseButton onClick={onClose}>
              <FiX />
            </CustomCloseButton>
            <ModalHeader $isDark={isDark}>
              <span className="modal-title">{title}</span>
            </ModalHeader>
            <ModalContent $isDark={isDark}>{children}</ModalContent>
            {footer && (
              <ModalFooter $isDark={isDark}>
                <ActionButtons $isDark={isDark}>{footer}</ActionButtons>
              </ModalFooter>
            )}
          </ContentCard>
        </StyledModalContent>
      </Modal>
    </>
  );
};

const ModalStyles = createGlobalStyle<{ showTwoColumns?: boolean; wide?: boolean }>`
  .export-quota-modal {
    &.show-two-columns {
      max-width: ${({ showTwoColumns }) => (showTwoColumns ? '900px' : '500px')} !important;
    }

    &.export-quota-modal-wide {
      max-width: 560px !important;
    }
  }

  .export-quota-modal .date-pick-dropdown {
    z-index: 2000 !important;
  }

  .export-quota-modal .transparent-modal-content {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
  }
  
  .export-quota-modal .modal-content {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
  }
`;

const StyledModalContent = styled.div`
  position: relative;
  background: transparent;
`;

const ContentCard = styled.div<{ $isDark: boolean }>`
  background: ${({ $isDark }) => ($isDark ? '#2E2E33' : 'white')};
  border-radius: 12px;
  margin: 20px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, ${({ $isDark }) => ($isDark ? '0.3' : '0.1')});
  position: relative;
  border: ${({ $isDark }) => ($isDark ? '1px solid #374151' : 'none')};
`;

const ModalHeader = styled.div<{ $isDark: boolean }>`
  padding: 24px 24px 0 24px;

  .modal-title {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: ${({ $isDark }) => ($isDark ? '#f9fafb' : '#374151')};
  }
`;

const CustomCloseButton = styled.button`
  position: absolute;
  top: -20px;
  right: -20px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #6b7280;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 10;

  svg {
    width: 20px;
    height: 20px;
    color: white;
  }

  &:hover {
    background: #4b5563;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ModalContent = styled.div<{ $isDark: boolean }>`
  padding: 5px 24px 10px 24px;
  color: ${({ $isDark }) => ($isDark ? '#f9fafb' : '#374151')};
`;

const ModalFooter = styled.div<{ $isDark: boolean }>`
  padding: 0 24px 24px 24px;
  background: ${({ $isDark }) => ($isDark ? '#2E2E33' : 'white')};
  border-radius: 0 0 12px 12px;
`;

const ActionButtons = styled.div<{ $isDark: boolean }>`
  display: flex;
  gap: 12px;
  justify-content: flex-end;

  .btn {
    padding: 12px 20px;
    font-weight: 500;
    transition: all 0.2s ease;
    border: none;
    cursor: pointer;

    &.btn-secondary {
      background: ${({ $isDark }) => ($isDark ? '#4b5563' : EXPORT_COLORS.secondary)};
      border-radius: 6px;
      color: white;

      &:hover:not(:disabled) {
        background: ${({ $isDark }) => ($isDark ? '#374151' : '#5a6268')};
      }
    }

    &.btn-primary,
    &.btn-success {
      background: #609954;
      border-radius: 10px;
      color: white;
      width: 100%;

      &:hover:not(:disabled) {
        background: #4f7d44;
      }
    }

    &.btn-outline-success {
      background: transparent;
      border: 1px solid #4caf50;
      color: #4caf50;
      border-radius: 6px;

      &:hover:not(:disabled) {
        background: #4caf50;
        color: white;
      }
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
`;
